import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import { useParentShell } from './ParentShell';

/**
 * Недельная сетка расписания ученика.
 *
 * Источник — `/api/parent/schedule?student_id=&year_id=&week=YYYY-MM-DD`.
 * `week` хранится в URL: при перелистывании страница шарится по ссылке
 * (см. конвенцию `useUrlState`). Если параметр пуст — берём текущую
 * неделю на сервере.
 *
 * По клику на ячейку с уроком — переход в дневник, в режим «предмет ×
 * месяц» (`/parent/journal?subject=<id>&month=YYYY-MM`).
 */
const URL_SCHEMA = { week: '' };

export default function ParentSchedulePage() {
  const { child, activeYear } = useParentShell();
  const [params, setParams]   = useUrlState(URL_SCHEMA);
  const navigate              = useNavigate();

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    const qs = new URLSearchParams({
      student_id: String(child.id),
      year_id:    String(activeYear.id),
    });
    if (params.week) qs.set('week', params.week);
    api.get(`/api/parent/schedule?${qs.toString()}`)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
  }, [child?.id, activeYear?.id, params.week]);

  function shiftWeek(deltaDays) {
    const base = data?.week?.start || params.week || todayIso();
    const next = addDays(base, deltaDays);
    setParams({ week: next });
  }
  function goToday() {
    setParams({ week: '' });
  }

  if (loading) {
    return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем расписание…</div>;
  }
  if (error) {
    return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  }
  if (!data) return null;

  const { weekdays = [], slots = [], lessons = [], subjects = [], holidays = [] } = data;

  // Слот может содержать несколько уроков с разными `effectiveFrom..To`
  // (см. админскую сетку). Храним массив и выбираем по дате ячейки.
  const gridByWeekday = {};
  for (const l of lessons) {
    gridByWeekday[l.weekday] = gridByWeekday[l.weekday] || {};
    const key = l.position;
    gridByWeekday[l.weekday][key] = gridByWeekday[l.weekday][key] || [];
    gridByWeekday[l.weekday][key].push(l);
  }

  // Каникулы / праздники: date -> {kind, label}. Скрываем уроки
  // на эти даты, шапку дня помечаем.
  const holidayByDate = {};
  for (const h of holidays) {
    if (h.kind === 'holiday' || h.kind === 'vacation' || h.kind === 'preholiday') {
      holidayByDate[h.date] = h;
    }
  }

  // weekdayNum приходит из колонки как scheduleOfN: в день переноса
  // выходного (рабочая суббота «за понедельник») уроки ищутся по дню
  // недели даты-источника.
  function pickLesson(weekdayNum, position, dateIso) {
    const h = holidayByDate[dateIso];
    if (h && (h.kind === 'holiday' || h.kind === 'vacation')) return null;
    const arr = gridByWeekday[weekdayNum]?.[position];
    if (!arr || arr.length === 0) return null;
    return arr.find((l) => {
      const from = l.effectiveFrom || '0000-01-01';
      const to   = l.effectiveTo   || '9999-12-31';
      return from <= dateIso && dateIso <= to;
    }) || null;
  }

  const weekTitle = weekRangeLabel(data.week);
  const todayIdx = weekdays.findIndex((w) => w.isToday);

  return (
    <div className="pc-body">
      <div className="pc-card">
        <div className="pc-card__head">
          <div className="pc-card__title">Неделя · {weekTitle}</div>
          <span className="pc-card__sub">
            {lessons.length} {pluralLessons(lessons.length)}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <button type="button" className="btn btn--sm" onClick={() => shiftWeek(-7)} title="Прошлая неделя">
              <Icon name="chevron-left" size={12} />
            </button>
            <button type="button" className="btn btn--sm" onClick={goToday}>Сегодня</button>
            <button type="button" className="btn btn--sm" onClick={() => shiftWeek(+7)} title="Следующая неделя">
              <Icon name="chevron" size={12} />
            </button>
          </div>
        </div>
        <div className="pc-card__body">
          <div className="pc-week" style={{ '--pc-days': weekdays.length }}>
            <div />
            {weekdays.map((d, i) => {
              const h = holidayByDate[d.date];
              return (
                <div
                  key={d.date}
                  className={'pc-week__head'
                    + (i === todayIdx ? ' is-today' : '')
                    + (h ? ' is-holiday' : '')}
                >
                  {d.label}
                  <span className="pc-week__head-num">{d.dateLabel}</span>
                  {d.transferredFrom && (
                    <span className="pc-week__head-holiday"
                          title={`Рабочий день: перенос выходного, занятия по расписанию ${d.transferredFromLabel}`}>
                      по расп. {d.transferredFromLabel}
                    </span>
                  )}
                  {h && (
                    <span className="pc-week__head-holiday" title={h.label || ''}>
                      {h.kind === 'vacation' ? 'Каникулы'
                        : h.kind === 'holiday' ? 'Выходной'
                        : 'Сокр.'}
                    </span>
                  )}
                </div>
              );
            })}
            {slots.map((slot, slotIdx) => (
              <React.Fragment key={slot.position}>
                <div className="pc-week__time">
                  <span className="pc-week__time-num">{slot.position}</span>
                  <span className="pc-week__time-range">{slot.timeStart}–{slot.timeEnd}</span>
                </div>
                {weekdays.map((d) => {
                  const lesson = pickLesson(d.scheduleOfN || d.n, slot.position, d.date);
                  if (!lesson) return <div key={d.date} className="pc-week-cell" />;
                  // Предметы с `no_journal` (продлёнка, динамическая пауза,
                  // самоподготовка и т.п.) — некликабельны: в дневнике по
                  // ним всё равно ничего нет.
                  const clickable = !lesson.subjectNoJournal;
                  return (
                    <div key={d.date} className="pc-week-cell">
                      <LessonChip
                        lesson={lesson}
                        onClick={clickable
                          ? () => {
                              const month = d.date.slice(0, 7);
                              navigate(`/parent/journal?subject=${lesson.subjectId}&month=${month}`);
                            }
                          : null}
                      />
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {slots.length === 0 && (
              <div style={{ gridColumn: '1 / -1', padding: '24px 0', textAlign: 'center', color: 'var(--pc-fg-faint)' }}>
                Для этого класса не настроены звонки.
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Сводка по предметам недели — компактная плашка. */}
      {subjects.length > 0 && (
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Предметы недели</div>
            <span className="pc-card__sub">
              {sumCounts(subjects)} занятий
            </span>
          </div>
          <div className="pc-card__body">
            <div className="pc-week-subjects">
              {subjects.map((s) => (
                <div key={s.id} className="pc-week-subj">
                  <span className="pc-week-subj__chip" style={{ background: s.color || '#888' }} />
                  <span className="pc-week-subj__name">{s.name}</span>
                  <span className="pc-week-subj__count">{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Карточка-урок в сетке. Цвет — лёгкий фон от subjectColor, текст — тёмный.
 * Если передан onClick — рендерим как <button> (кликом ведём в дневник),
 * иначе — обычный <div> без курсора и hover-эффекта (для предметов
 * с no_journal вроде самоподготовки).
 */
function LessonChip({ lesson, onClick }) {
  const bg = chipBg(lesson.subjectColor);
  const content = (
    <>
      <div className="pc-lesson-chip__name">{lesson.subjectName}</div>
      {lesson.teacher && <div className="pc-lesson-chip__topic">{lesson.teacher}</div>}
      {lesson.room && <div className="pc-lesson-chip__room">каб. {lesson.room}</div>}
    </>
  );
  if (!onClick) {
    return (
      <div
        className="pc-lesson-chip is-static"
        style={{ background: bg, color: 'var(--pc-fg)' }}
      >
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className="pc-lesson-chip"
      style={{
        background: bg,
        color: 'var(--pc-fg)',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        width: '100%',
      }}
    >
      {content}
    </button>
  );
}

function chipBg(hex) {
  if (!hex || !/^#[0-9a-fA-F]{3,8}$/.test(hex)) return 'oklch(0.96 0.02 220)';
  // hex + alpha 26 (~15%) — лёгкая подложка.
  return hex.length === 4
    ? `${hex}26` // #fff → #fff26 не валидно; fallback ниже
    : `${hex}26`;
}

function weekRangeLabel(week) {
  if (!week?.start) return '';
  const months = ['янв','фев','мар','апр','май','июн','июл','авг','сен','окт','ноя','дек'];
  const a = new Date(week.start);
  // У нас в API end — это воскресенье; для шапки достаточно Пт.
  // Возьмём дельту на 4 дня (Пн+4 = Пт), но если sun — оставим воскресенье.
  const b = new Date(a.getTime() + 4 * 86400000);
  const da = a.getUTCDate();
  const db = b.getUTCDate();
  const ma = months[a.getUTCMonth()];
  const mb = months[b.getUTCMonth()];
  return ma === mb ? `${da}–${db} ${ma}` : `${da} ${ma} – ${db} ${mb}`;
}

// Локальные YYYY-MM-DD, без UTC-сдвига. Подробнее — в комментарии
// в SchedulePage.jsx (та же проблема: toISOString отдаёт UTC, и в MSK
// «00:00 локально» становится «21:00 предыдущих суток»).
function fmtLocalIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function addDays(iso, delta) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + delta);
  return fmtLocalIso(d);
}
function todayIso() { return fmtLocalIso(new Date()); }

function pluralLessons(n) {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return 'уроков';
  if (m10 === 1) return 'урок';
  if (m10 >= 2 && m10 <= 4) return 'урока';
  return 'уроков';
}

function sumCounts(arr) {
  return arr.reduce((s, x) => s + (x.count || 0), 0);
}
